/**
 * What a card spec MEANS, once, for both places that answer it.
 *
 * A Dashboard card is answered on the device from its SQLite mirror
 * (`apps/mobile/src/lib/insight-query.ts`) and, when the device no longer
 * holds the rows a card asks about, on the server from Postgres
 * (`apps/server/src/insights/answer-sql.ts`). The two SQL dialects differ, so
 * each side builds its own statement; what a spec asks — which days a period
 * covers, which cards a day bucket can answer, which cards the server may be
 * asked, how a ranked answer is cut — is pure and lives here, so the two
 * cannot drift into two meanings (owner decision, 29 Oct 2026).
 *
 * Both statements are tested against the same fixture rows
 * (`insight-answer.fixture.ts`), which is how "the same meaning" is proved
 * rather than promised.
 *
 * Pure: no database, no clock. A day is `YYYY-MM-DD`, the workspace's.
 */
import { aggLeavesOf } from './setup-draft/cards';
import {
  readInsightAgg,
  readInsightWhere,
  readRowSource,
  type InsightAggNode,
  type InsightRowSource,
  type InsightWhere,
} from './insights';

/* ------------------------------------------------------------------- spec */

/** Which number the aggregate reads: the record's measure, one field, or a row formula. */
export type InsightSource = InsightRowSource;

/**
 * What to compute. `rows` counts rows; `formula` is the tree whose leaves are
 * totals, one per record it reads (`insight.cross_record`).
 */
export type InsightMeasure =
  | { readonly agg: 'sum' | 'avg' | 'min' | 'max' | 'count'; readonly of: InsightSource }
  | { readonly agg: 'rows' }
  | { readonly agg: 'formula'; readonly expr: InsightAggNode };

/** The window. 'all' is every row held, and the only scope a dateless entry reaches. */
export type InsightScope = 'day' | 'week' | 'month' | 'year' | 'all';

/** A scope that has a period before it, so a pair can compare against one. */
export type InsightPairScope = Exclude<InsightScope, 'all'>;

/** One condition on the rows a card counts: the shared shape. */
export type InsightFilter = InsightWhere;

type Filtered = {
  readonly filters?: readonly InsightFilter[];
  /** A date field of the record whose day the period is read on, in place of the entry's own day (F20). */
  readonly dateField?: string;
};

/** One number; with `leaderOf`, the largest group's number and its key. */
export type ScalarSpec = Filtered & {
  readonly shape: 'scalar';
  readonly measure: InsightMeasure;
  readonly scope: InsightScope;
  readonly leaderOf?: string;
  readonly leaderVia?: string;
};

/** The same number twice: this period, and the one before it. */
export type PairSpec = Filtered & {
  readonly shape: 'scalar_pair';
  readonly measure: InsightMeasure;
  readonly scope: InsightPairScope;
};

/** One number per day, for the last `days` days, today included. */
export type InsightSeriesSpec = Filtered & {
  readonly shape: 'series';
  readonly measure: InsightMeasure;
  readonly days: number;
};

/** One number per group, ranked by size. `limit` is how many the card draws. */
export type BreakdownSpec = Filtered & {
  readonly shape: 'breakdown';
  readonly measure: InsightMeasure;
  readonly groupFieldId: string;
  readonly groupVia?: string;
  readonly scope: InsightScope;
  readonly limit: number;
};

export type InsightSpec = ScalarSpec | PairSpec | InsightSeriesSpec | BreakdownSpec;

/* ----------------------------------------------------------------- results */

export type ScalarResult = {
  readonly shape: 'scalar';
  readonly value: number | null;
  /** The winning group's stored key when the spec named `leaderOf`, else null. */
  readonly key: string | null;
};

export type PairResult = { readonly shape: 'scalar_pair'; readonly value: number | null; readonly against: number | null };

export type InsightSeriesPoint = { readonly day: string; readonly value: number };

export type SeriesResult = { readonly shape: 'series'; readonly points: readonly InsightSeriesPoint[] };

export type BreakdownSlice = { readonly key: string; readonly value: number };

export type BreakdownResult = {
  readonly shape: 'breakdown';
  readonly slices: readonly BreakdownSlice[];
  /** Everything past `limit`, netted. Null when nothing was left over. */
  readonly other: { readonly count: number; readonly value: number } | null;
  /** Set when the groups overlap (a multi select): the measure over each entry once. */
  readonly overlap?: { readonly total: number | null };
};

export type InsightResult = ScalarResult | PairResult | SeriesResult | BreakdownResult;

/** The most groups one breakdown statement will scan: a bound on the work, not on the answer. */
export const GROUP_SCAN_LIMIT = 500;

/* -------------------------------------------------------------- day windows */

const DAY_MS = 86_400_000;

const dayToUtc = (day: string): number =>
  Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10)));

const utcToDay = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

/** `day` moved by `count` days. */
export const addDays = (day: string, count: number): string => utcToDay(dayToUtc(day) + count * DAY_MS);

/** Monday, per ISO 8601. */
const weekStart = (day: string): string => {
  const weekday = new Date(dayToUtc(day)).getUTCDay();
  return addDays(day, -((weekday + 6) % 7));
};

/** Day 0 of the next month is the last day of this one. */
const monthEnd = (year: number, monthIndex: number): string => utcToDay(Date.UTC(year, monthIndex + 1, 0));

/** A span of days, both ends included. */
export type DayWindow = { readonly from: string; readonly to: string };

/** The window one scope covers, ending at the period's own end rather than at today. */
export const windowFor = (scope: InsightPairScope, today: string): DayWindow => {
  const year = Number(today.slice(0, 4));
  const monthIndex = Number(today.slice(5, 7)) - 1;
  switch (scope) {
    case 'day':
      return { from: today, to: today };
    case 'week': {
      const from = weekStart(today);
      return { from, to: addDays(from, 6) };
    }
    case 'month':
      return { from: `${today.slice(0, 7)}-01`, to: monthEnd(year, monthIndex) };
    case 'year':
      return { from: `${today.slice(0, 4)}-01-01`, to: `${today.slice(0, 4)}-12-31` };
  }
};

/** The window immediately before the one `windowFor` answers. */
export const previousWindowFor = (scope: InsightPairScope, today: string): DayWindow => {
  const current = windowFor(scope, today);
  switch (scope) {
    case 'day':
      return { from: addDays(current.from, -1), to: addDays(current.from, -1) };
    case 'week':
      return { from: addDays(current.from, -7), to: addDays(current.from, -1) };
    case 'month': {
      const year = Number(current.from.slice(0, 4));
      const monthIndex = Number(current.from.slice(5, 7)) - 1;
      const from = utcToDay(Date.UTC(year, monthIndex - 1, 1));
      return { from, to: monthEnd(Number(from.slice(0, 4)), Number(from.slice(5, 7)) - 1) };
    }
    case 'year': {
      const year = Number(current.from.slice(0, 4)) - 1;
      return { from: `${year}-01-01`, to: `${year}-12-31` };
    }
  }
};

/** The window a series covers: `days` days back from today, today included. */
export const seriesWindow = (days: number, today: string): DayWindow => ({
  from: addDays(today, -(Math.max(1, Math.trunc(days)) - 1)),
  to: today,
});

/**
 * The window a scalar or breakdown counts: an outside window when one was
 * handed in (it replaces the scope), else the scope's, else null for 'all'.
 */
export const cardWindowOf = (spec: ScalarSpec | BreakdownSpec, today: string, outside: DayWindow | null = null): DayWindow | null =>
  outside ?? (spec.scope === 'all' ? null : windowFor(spec.scope, today));

/**
 * The earliest day a card asks about, or null when it asks about all of them.
 * One answer per shape, matching what the shape's own statement reads.
 */
export const earliestDayOf = (spec: InsightSpec, today: string, outside: DayWindow | null = null): string | null => {
  if (outside !== null) return outside.from;
  switch (spec.shape) {
    case 'series':
      return seriesWindow(spec.days, today).from;
    case 'scalar_pair':
      return previousWindowFor(spec.scope, today).from;
    case 'scalar':
    case 'breakdown':
      return spec.scope === 'all' ? null : windowFor(spec.scope, today).from;
  }
};

/* ---------------------------------------------------------------- buckets */

/**
 * Can this card be answered from the day buckets at all, and from which column?
 *
 * A bucket holds one number per record per day — the record's own measure,
 * summed, and the row count. No group, no filter, no second record. So yes
 * for "how much" and "how many" of one record with nothing attached, and no
 * for everything else: a breakdown or a filtered total summed out of a
 * dimension that is not there is a wrong number under a confident label.
 */
export const bucketMeasure = (spec: InsightSpec): 'value' | 'rows' | null => {
  if (spec.filters !== undefined && spec.filters.length > 0) return null;
  if (spec.shape === 'breakdown') return null;
  if (spec.shape === 'scalar' && spec.leaderOf !== undefined) return null;
  if (spec.measure.agg === 'rows') return 'rows';
  if (spec.measure.agg !== 'sum') return null;
  return spec.measure.of.kind === 'measure' ? 'value' : null;
};

/** Every record a card's totals read: its host, and each leaf's own record. */
export const cardRecordIds = (host: string, spec: InsightSpec): readonly string[] =>
  spec.measure.agg !== 'formula'
    ? [host]
    : [...new Set([host, ...aggLeavesOf(spec.measure.expr).map((leaf) => leaf.recordId ?? host)])];

/* -------------------------------------------------------- the server's part */

/** The shapes the server answers: one number, or one number per group. */
export type ServerInsightSpec = ScalarSpec | BreakdownSpec;

/**
 * The longest window the server totals: one calendar year (W26, owner
 * decision 29 Oct 2026). A flow card reads today, this week, this month, last
 * month or this year; 'all time' is not a flow card's period.
 */
export const withinCalendarYear = (window: DayWindow): boolean => {
  if (!(window.from <= window.to)) return false;
  const from = window.from;
  const nextYear = `${String(Number(from.slice(0, 4)) + 1).padStart(4, '0')}${from.slice(4)}`;
  // Feb 29 has no twin next year; a year from it ends on Feb 28.
  const end = addDays(nextYear.endsWith('-02-29') ? `${nextYear.slice(0, 8)}28` : nextYear, nextYear.endsWith('-02-29') ? 0 : -1);
  return window.to <= end;
};

/** Why the server will not answer a card, or null when it will. */
export type ServerInsightProblem =
  | 'INSIGHT_SHAPE'
  | 'INSIGHT_WINDOW_ALL'
  | 'INSIGHT_WINDOW_TOO_LONG'
  | 'INSIGHT_DATE_FIELD_COMBINED';

/**
 * The window the server would total a card over, or why it will not: only a
 * scalar (with no leader) or a breakdown, never over all time, never longer
 * than a calendar year.
 */
export const serverWindowOf = (
  spec: InsightSpec,
  today: string,
  outside: DayWindow | null = null,
): { readonly ok: true; readonly window: DayWindow } | { readonly ok: false; readonly problem: ServerInsightProblem } => {
  if (spec.shape !== 'scalar' && spec.shape !== 'breakdown') return { ok: false, problem: 'INSIGHT_SHAPE' };
  if (spec.shape === 'scalar' && spec.leaderOf !== undefined) return { ok: false, problem: 'INSIGHT_SHAPE' };
  if (spec.measure.agg === 'formula' && spec.dateField !== undefined) return { ok: false, problem: 'INSIGHT_DATE_FIELD_COMBINED' };
  const window = cardWindowOf(spec, today, outside);
  if (window === null) return { ok: false, problem: 'INSIGHT_WINDOW_ALL' };
  if (!withinCalendarYear(window)) return { ok: false, problem: 'INSIGHT_WINDOW_TOO_LONG' };
  return { ok: true, window };
};

/**
 * Whether the device must ask the server for this card: it reaches below the
 * floor of a record it reads (the rows are gone), the day buckets cannot
 * answer it, and the server would (a scalar or a breakdown within a calendar
 * year). Anything else is answered on the device, or draws the quiet
 * placeholder.
 *
 * `floorDay` is the card's floor: the latest floor among every record it reads.
 */
export const asksServer = (spec: InsightSpec, today: string, floorDay: string | null, outside: DayWindow | null = null): boolean => {
  if (floorDay === null) return false;
  // A card read on a date field of its rows asks the rows the device holds, as it always has.
  if (spec.dateField !== undefined) return false;
  const earliest = earliestDayOf(spec, today, outside);
  if (earliest !== null && earliest >= floorDay) return false;
  if (spec.measure.agg !== 'formula' && bucketMeasure(spec) !== null) return false;
  return serverWindowOf(spec, today, outside).ok;
};

/**
 * How long a kept server answer is shown without asking again: fifteen
 * minutes. The device also asks again the moment a sync changes any record
 * the card reads, so the clock only covers what a sync cannot see — another
 * person's rows this device does not hold, and the totals they move. Long
 * enough that opening the Dashboard twice does not total a year in Postgres
 * twice; short enough that a figure on screen is never an afternoon old.
 */
export const INSIGHT_ANSWER_FRESH_MS = 15 * 60 * 1000;

/** Whether a kept answer is still fresh: young enough, the same day, and the same records' state. */
export const keptAnswerFresh = (
  kept: { readonly computedAt: string; readonly day: string; readonly reads: string },
  now: { readonly at: Date; readonly day: string; readonly reads: string },
): boolean => {
  const age = now.at.getTime() - new Date(kept.computedAt).getTime();
  return Number.isFinite(age) && age >= 0 && age < INSIGHT_ANSWER_FRESH_MS && kept.day === now.day && kept.reads === now.reads;
};

/* ----------------------------------------------------------- the wire */

/** POST /v1/insights/answer: one card, over every current entry of the records it reads. */
export type InsightAnswerRequest = {
  /** The record whose fields the spec names (the card's host). */
  readonly recordId: string;
  readonly spec: ServerInsightSpec;
  /** The workspace's zone; the server reads today in it, as the device does. */
  readonly zone?: string;
  /** The device's own day (`workspaceToday`), YYYY-MM-DD: the server reads the period on it, so both keep the same day. */
  readonly today?: string;
  /** An outside window, replacing the spec's own period. */
  readonly window?: DayWindow;
};

export type InsightAnswerResponse = {
  readonly result: ScalarResult | BreakdownResult;
  /** When the server worked it out, an ISO instant. */
  readonly computedAt: string;
  /** The window it covered. */
  readonly window: DayWindow;
};

const SCOPES: ReadonlySet<string> = new Set(['day', 'week', 'month', 'year', 'all']);
const DAY = /^\d{4}-\d{2}-\d{2}$/;

const readFilters = (raw: unknown): readonly InsightFilter[] | null | undefined => {
  if (raw === undefined) return undefined;
  if (!Array.isArray(raw)) return null;
  const out: InsightFilter[] = [];
  for (const one of raw) {
    const read = readInsightWhere(one);
    if (read === null) return null;
    out.push(read);
  }
  return out;
};

const readMeasure = (raw: unknown): InsightMeasure | null => {
  if (typeof raw !== 'object' || raw === null) return null;
  const held = raw as { agg?: unknown; of?: unknown; expr?: unknown };
  if (held.agg === 'rows') return { agg: 'rows' };
  if (held.agg === 'formula') {
    const expr = readInsightAgg(held.expr);
    return expr === null ? null : { agg: 'formula', expr };
  }
  if (held.agg === 'sum' || held.agg === 'avg' || held.agg === 'min' || held.agg === 'max' || held.agg === 'count') {
    const of = readRowSource(held.of);
    return of === null ? null : { agg: held.agg, of };
  }
  return null;
};

const optionalId = (raw: unknown): string | null | undefined =>
  raw === undefined ? undefined : typeof raw === 'string' && raw !== '' ? raw : null;

/** A day window off the wire, or null. */
export const readDayWindow = (raw: unknown): DayWindow | null => {
  if (typeof raw !== 'object' || raw === null) return null;
  const held = raw as { from?: unknown; to?: unknown };
  if (typeof held.from !== 'string' || typeof held.to !== 'string') return null;
  if (!DAY.test(held.from) || !DAY.test(held.to) || held.from > held.to) return null;
  return { from: held.from, to: held.to };
};

/**
 * A scalar or breakdown spec off the wire, read without trusting it. Null for
 * anything this build cannot answer as said; never a guess.
 */
export const readServerInsightSpec = (raw: unknown): ServerInsightSpec | null => {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const held = raw as Record<string, unknown>;
  const measure = readMeasure(held.measure);
  if (measure === null) return null;
  if (typeof held.scope !== 'string' || !SCOPES.has(held.scope)) return null;
  const scope = held.scope as InsightScope;
  const filters = readFilters(held.filters);
  if (filters === null) return null;
  const dateField = optionalId(held.dateField);
  if (dateField === null) return null;
  const extra = {
    ...(filters === undefined || filters.length === 0 ? {} : { filters }),
    ...(dateField === undefined ? {} : { dateField }),
  };
  if (held.shape === 'scalar') {
    if (held.leaderOf !== undefined || held.leaderVia !== undefined) return null;
    return { shape: 'scalar', measure, scope, ...extra };
  }
  if (held.shape === 'breakdown') {
    if (typeof held.groupFieldId !== 'string' || held.groupFieldId === '') return null;
    if (typeof held.limit !== 'number' || !Number.isFinite(held.limit) || held.limit < 1) return null;
    const groupVia = optionalId(held.groupVia);
    if (groupVia === null) return null;
    return {
      shape: 'breakdown',
      measure,
      scope,
      groupFieldId: held.groupFieldId,
      limit: Math.min(GROUP_SCAN_LIMIT, Math.trunc(held.limit)),
      ...(groupVia === undefined ? {} : { groupVia }),
      ...extra,
    };
  }
  return null;
};

/* ------------------------------------------------------------- row shaping */

/** One row of a scalar statement. */
export type ScalarRow = { value: number | string | null; key?: string | number | null };
/** One row of a series or breakdown statement. */
export type GroupedRow = { key?: string | number | null; day?: string | null; value: number | string | null };

const asKey = (raw: string | number | null | undefined): string | null =>
  raw === null || raw === undefined ? null : String(raw);

/** A number off either driver: SQLite answers a number, Postgres a numeric as text. */
const asValue = (raw: number | string | null | undefined): number | null => {
  if (raw === null || raw === undefined) return null;
  const value = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(value) ? value : null;
};

/** The top `limit` slices, and everything behind them netted into one. */
export const cutBreakdown = (rows: readonly GroupedRow[], limit: number): BreakdownResult => {
  const cap = Math.max(1, Math.trunc(limit));
  const slices: BreakdownSlice[] = [];
  let otherCount = 0;
  let otherValue = 0;
  for (const row of rows) {
    const key = asKey(row.key);
    if (key === null) continue;
    const value = asValue(row.value) ?? 0;
    if (slices.length < cap) {
      slices.push({ key, value });
      continue;
    }
    otherCount += 1;
    otherValue += value;
  }
  return { shape: 'breakdown', slices, other: otherCount === 0 ? null : { count: otherCount, value: otherValue } };
};

/** A scalar statement's rows, as a result. No rows means nothing matched. */
export const readScalar = (rows: readonly ScalarRow[]): ScalarResult => {
  const row = rows[0];
  return { shape: 'scalar', value: asValue(row?.value), key: asKey(row?.key) };
};

/* ------------------------------------------------------- the ledger source */

/**
 * A money card that reads the ledger (L39 of docs/plans/ledger.md): a figure
 * of the business, not a total over one record. The phone holds no ledger
 * lines (L14), so each is answered on the server by `POST /v1/insights/answer`
 * and says "Worked out at". Reading one needs `see_money` (L27).
 *
 *   profit           income less expenses over the window (L51); drawings,
 *                    transfers, loans and To sort are not in it
 *   owed_to_you      what customers owe: the parties above nought on the
 *                    receivable, added (L25)
 *   paid_in_advance  the parties below nought on the receivable, never netted
 *                    with the ones above (L25)
 *   you_owe          what is owed to suppliers: the parties below nought on
 *                    the payable
 *   cash_and_bank    the cash and bank accounts, today
 *   to_sort          the money in To sort, and the entries that hold it (L17)
 *   tax_to_pay       tax collected less tax you can claim back, for the tax
 *                    period (the month) the day is in (L43)
 */
export const LEDGER_FIGURES = ['profit', 'owed_to_you', 'paid_in_advance', 'you_owe', 'cash_and_bank', 'to_sort', 'tax_to_pay'] as const;
export type LedgerFigure = (typeof LEDGER_FIGURES)[number];

export const isLedgerFigure = (value: unknown): value is LedgerFigure =>
  typeof value === 'string' && (LEDGER_FIGURES as readonly string[]).includes(value);

/** The window profit reads: this month, or this financial year (L42). The others are balances on the day. */
export type LedgerScope = 'month' | 'fiscal_year';

export type LedgerCardSpec = {
  readonly ledger: LedgerFigure;
  /** Profit only. Absent: this month. */
  readonly scope?: LedgerScope;
};

/** A ledger card off the wire, or null. */
export const readLedgerCardSpec = (raw: unknown): LedgerCardSpec | null => {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const held = raw as { ledger?: unknown; scope?: unknown };
  if (!isLedgerFigure(held.ledger)) return null;
  if (held.scope === undefined) return { ledger: held.ledger };
  if (held.scope !== 'month' && held.scope !== 'fiscal_year') return null;
  return { ledger: held.ledger, scope: held.scope };
};

/** The month the day is in, its first and last day. */
export const monthWindowOf = (today: string): DayWindow => {
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7));
  const next = month === 12 ? `${year + 1}-01-01` : `${today.slice(0, 5)}${String(month + 1).padStart(2, '0')}-01`;
  return { from: `${today.slice(0, 7)}-01`, to: addDays(next, -1) };
};

/**
 * The days a ledger card covers, or null for a balance on the day (every
 * line up to today). `yearFrom` is the first day of the financial year the
 * day is in, from `fiscalYearBounds`.
 */
export const ledgerWindowOf = (spec: LedgerCardSpec, today: string, yearFrom: string | null): DayWindow | null => {
  if (spec.ledger === 'tax_to_pay') return monthWindowOf(today);
  if (spec.ledger !== 'profit') return null;
  if (spec.scope === 'fiscal_year' && yearFrom !== null) return { from: yearFrom, to: today };
  return monthWindowOf(today);
};

/** One named part of a ledger figure: an account, or the party that owes the most. */
export type LedgerPart = { readonly id: string; readonly name: string | null; readonly value: number };

export type LedgerResult = {
  readonly shape: 'ledger';
  readonly figure: LedgerFigure;
  /** The figure, in the workspace's currency. Null when the workspace keeps no accounts yet. */
  readonly value: number | null;
  readonly unit: string | null;
  /** How many: customers owed, suppliers owing, entries in To sort. */
  readonly count: number;
  /** The largest part, said under the figure: "Kavya Clinic: ₹4,200". */
  readonly leader: LedgerPart | null;
  /** The accounts it adds, for Cash and bank: "Cash ₹18,640 · Bank ₹2,36,450". */
  readonly parts: readonly LedgerPart[];
  /** Profit only: what waits in To sort, which may still be an expense (the kirana test). */
  readonly toSort: { readonly count: number; readonly value: number } | null;
  /**
   * Tax to pay only (step 12.7): the Tax payment record its foot opens, and
   * the day the month's tax is due where the regime fixes one (`YYYY-MM-DD`;
   * GST's 20th of the next month), else null. Absent when there is no Tax
   * payment record. Optional on the wire.
   */
  readonly taxPayment?: { readonly recordId: string; readonly due: string | null } | null;
};

/** POST /v1/insights/answer with a ledger card in place of a record's spec. */
export type LedgerAnswerRequest = {
  readonly ledger: LedgerCardSpec;
  readonly zone?: string;
  readonly today?: string;
};

export type LedgerAnswerResponse = {
  readonly result: LedgerResult;
  readonly computedAt: string;
  /** The days it covered; null for a balance on the day. */
  readonly window: DayWindow | null;
};
